import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw } from 'lucide-react';
import AffiliateProductCard, { money } from './AffiliateProductCard';

const API = process.env.REACT_APP_BACKEND_URL;

// Rank a category's products for a given slot: items matching a "prefer"
// keyword (and no "avoid" keyword) first, then the rest. The ranked list also
// powers "swap" — advancing the index walks down to the next best option.
const rankCandidates = (list, need) => {
  const txt = (p) => `${p.name || ''} ${p.kind || ''}`.toLowerCase();
  const ok = (p) => !(need.avoid || []).some((a) => txt(p).includes(a));
  const pref = (p) => (need.prefer || []).some((k) => txt(p).includes(k));
  const good = list.filter(ok);
  const preferred = good.filter(pref);
  const rest = good.filter((p) => !pref(p));
  const ranked = [...preferred, ...rest];
  return ranked.length ? ranked : list;
};

const priceNum = (p) => {
  const n = parseFloat(String(p?.price ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * MatchedProjectBundle — a single "matched" set for a project (not a store
 * grid): one picked product per need, with a running total and per-slot "swap"
 * so the set feels personalised. Config-driven so bathroom/kitchen reuse it.
 */
const MatchedProjectBundle = ({ eyebrow, title, intro, seeAll, guideLink, needs, testid, Icon, department }) => {
  const [slots, setSlots] = useState(null); // [{ label, ranked:[], index }]

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          needs.map(async (n) => {
            const dep = n.department || department || 'bathroom-reno';
            const brandQ = n.brand ? `&brand=${encodeURIComponent(n.brand)}` : '';
            const r = await fetch(`${API}/api/affiliate/browse?department=${dep}&category=${n.cat}&per_page=48&sort=featured${brandQ}`);
            if (!r.ok) return null;
            const j = await r.json();
            const ranked = rankCandidates(j.products || [], n);
            return ranked.length ? { label: n.label, ranked, index: 0 } : null;
          })
        );
        if (!cancelled) setSlots(results.filter(Boolean));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = useMemo(() => {
    if (!slots) return 0;
    return slots.reduce((sum, s) => sum + priceNum(s.ranked[s.index]), 0);
  }, [slots]);

  const swap = (i) => {
    setSlots((prev) => prev.map((s, idx) =>
      idx === i ? { ...s, index: (s.index + 1) % s.ranked.length } : s
    ));
  };

  if (!slots || slots.length < 3) return null;

  return (
    <section className="py-12 sm:py-16 bg-white" data-testid={testid}>
      <div className="max-w-[1100px] mx-auto px-5 sm:px-6">
        {eyebrow && (
          <div className="text-[12px] font-semibold text-[#1d8efe] mb-2">{eyebrow}</div>
        )}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div className="max-w-[660px]">
            <h2 className="text-[22px] sm:text-[26px] font-bold text-[#1d1d1f] tracking-tight">{title}</h2>
            <p className="text-[13.5px] text-[#6b7078] mt-1.5 leading-[1.5]">{intro}</p>
          </div>
          {total > 0 && (
            <div className="text-right shrink-0" data-testid={`${testid}-total`}>
              <p className="text-[11px] uppercase tracking-[0.05em] text-[#9aa0a8] font-semibold">Matched set from</p>
              <p className="text-[28px] font-bold text-[#1d1d1f] leading-none">{money(total)}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {slots.map((s, i) => {
            const product = s.ranked[s.index];
            return (
              <div key={`${s.label}-${product.id}`} className="flex flex-col">
                <div className="flex items-center justify-between mb-1.5 pl-0.5">
                  <p className="text-[11px] font-semibold text-[#52525b]">{s.label}</p>
                  {s.ranked.length > 1 && (
                    <button
                      type="button"
                      onClick={() => swap(i)}
                      className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#1d8efe] hover:text-[#0b6fd6] transition-colors"
                      data-testid={`${testid}-swap-${i}`}
                      aria-label={`Swap ${s.label}`}
                    >
                      <RefreshCw className="w-3 h-3" /> Swap
                    </button>
                  )}
                </div>
                <AffiliateProductCard product={product} />
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
          {seeAll && (
            <Link
              to={seeAll.to}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1d8efe] hover:gap-2.5 transition-all"
              data-testid={`${testid}-see-all`}
            >
              {seeAll.label} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          {guideLink && (
            <Link
              to={guideLink.to}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1d1d1f] hover:text-[#1d8efe] transition-colors"
              data-testid={`${testid}-guide-link`}
            >
              {guideLink.label} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <p className="text-[11.5px] text-[#9aa0a8] leading-[1.5]">
            Tap <span className="font-semibold text-[#6b7078]">Swap</span> to match a different piece. BuildPilot may earn a commission if you buy from a matched retailer — it never changes what we match you with.
          </p>
        </div>
      </div>
    </section>
  );
};

export default MatchedProjectBundle;
